import { Avatar, AvatarFallback, AvatarImage } from "@/ui/components/ui/avatar";
import {
  LayoutDashboard,
  MapPin,
  CalendarDays,
  Building2,
  Users,
  User,
  Bell,
} from "lucide-react";
import { cn } from "@/utils/utils";
import logoBrain from "@/assets/logo-brain.png";

interface SidebarProps {
  readonly user: {
    readonly full_name: string;
    readonly level: number;
    readonly xp: number;
    readonly total_progress_percentage: number;
    readonly avatar_url?: string;
  };
  readonly unreadNotifications: number;
  readonly currentPath: string;
  readonly onNavigate: (path: string) => void;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  path: string;
}

const mainMenuItems: MenuItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    path: "/dashboard",
  },
  { id: "temples", label: "Templos", icon: MapPin, path: "/jornada" },
  {
    id: "calendario",
    label: "Calendário",
    icon: CalendarDays,
    path: "/calendario",
  },
  {
    id: "business",
    label: "Meu Negócio",
    icon: Building2,
    path: "/meu-negocio",
  },
  { id: "mentors", label: "Meu Mentor", icon: Users, path: "/mentor" },
  { id: "profile", label: "Perfil", icon: User, path: "/perfil" },
];

export function DashboardSidebar({
  user,
  unreadNotifications,
  currentPath,
  onNavigate,
}: SidebarProps) {
  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  return (
    <div
      data-tour="sidebar"
      className="w-64 h-screen bg-white/95 backdrop-blur-sm flex flex-col fixed left-0 top-0 border-r border-black/[0.06] z-50"
    >
      {/* Logo */}
      <div className="px-5 pt-5 pb-4 flex-shrink-0">
        <div
          className="flex items-center gap-2.5 cursor-pointer"
          onClick={() => onNavigate("/dashboard")}
        >
          <img
            src={logoBrain}
            alt="Logo"
            className="w-10 h-10 object-contain"
          />
          <div className="leading-none">
            <h2 className="text-[11px] font-extrabold text-neutral-800 tracking-[0.04em]">
              ENGENHARIA DO SIM
            </h2>
            <p className="text-[9px] text-accent-800/70 font-semibold tracking-[0.08em] mt-0.5">
              PREMIUM
            </p>
          </div>
        </div>
      </div>

      {/* User Profile Card */}
      <div className="mx-3 mb-1 flex-shrink-0">
        <div className="rounded-xl bg-gradient-to-b from-neutral-50/80 to-white border border-black/[0.04] p-3.5 shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
          <div className="flex items-center gap-3">
            <div className="relative flex-shrink-0">
              <Avatar className="w-11 h-11 ring-[1.5px] ring-black/[0.06] ring-offset-[1.5px] ring-offset-white">
                <AvatarImage src={user.avatar_url} className="object-cover" />
                <AvatarFallback className="bg-gradient-to-br from-accent-800 to-accent-600 text-white font-bold text-xs">
                  {getInitials(user.full_name)}
                </AvatarFallback>
              </Avatar>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigate("/notificacoes");
                }}
                className="absolute -bottom-0.5 -right-0.5 w-6 h-6 bg-accent-800 rounded-full border-2 border-white flex items-center justify-center hover:bg-accent-700 transition-colors shadow-sm"
                title="Notificações"
              >
                <Bell className="w-3 h-3 text-white" />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 bg-red-500 text-white text-[8px] rounded-full flex items-center justify-center font-bold px-0.5 shadow-sm">
                    {unreadNotifications > 9 ? "9+" : unreadNotifications}
                  </span>
                )}
              </button>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-neutral-800 truncate leading-tight">
                {user.full_name}
              </p>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-[10px] font-bold text-accent-800 bg-accent-800/[0.07] px-1.5 py-[2px] rounded-[4px] tracking-wide">
                  NV {user.level}
                </span>
                <span className="text-[10px] text-neutral-400 font-medium tabular-nums">
                  {user.xp} XP
                </span>
              </div>
            </div>
          </div>

          {/* Progress */}
          <div className="mt-3 pt-3 border-t border-black/[0.04]">
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-[10px] text-neutral-400 font-semibold uppercase tracking-wider">
                Progresso
              </span>
              <span className="text-[11px] text-accent-800 font-bold tabular-nums">
                {Math.round(user.total_progress_percentage)}%
              </span>
            </div>
            <div className="h-[5px] w-full bg-black/[0.04] rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-accent-800 to-accent-600 rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${user.total_progress_percentage}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 pt-4 pb-4">
        <p className="px-3 text-[10px] font-bold text-neutral-300 uppercase tracking-[0.08em] mb-2">
          Navegação
        </p>
        <div className="space-y-[2px]">
          {mainMenuItems.map((item) => {
            const isActive =
              currentPath === item.path ||
              (item.id === "dashboard" && currentPath === "/");
            const Icon = item.icon;

            return (
              <button
                key={item.id}
                onClick={() => onNavigate(item.path)}
                className={cn(
                  "w-full flex items-center gap-2.5 px-3 py-[9px] rounded-[10px] text-left transition-all duration-150 relative group",
                  isActive
                    ? "bg-accent-800/[0.06] text-neutral-900"
                    : "text-neutral-500 hover:bg-black/[0.02] hover:text-neutral-700",
                )}
              >
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-[2.5px] bg-accent-800 rounded-r-full" />
                )}
                <Icon
                  className={cn(
                    "w-[16px] h-[16px] transition-colors duration-150",
                    isActive
                      ? "text-accent-800"
                      : "text-neutral-400 group-hover:text-neutral-500",
                  )}
                />
                <span
                  className={cn(
                    "text-[13px] leading-none",
                    isActive ? "font-semibold" : "font-medium",
                  )}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-black/[0.04] flex-shrink-0">
        <p className="text-[9px] text-neutral-300 text-center font-medium">
          &copy; {new Date().getFullYear()} Engenharia do SIM
        </p>
      </div>
    </div>
  );
}
